import { z } from 'zod/v3';
import { ToolModel } from './tool.model';
import { GraphModel } from '../graph/graph.model';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import { ActionResult, AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { err, ok } from 'neverthrow';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';
import { ModalPageView } from '@cotera/client/app/components/app';

type Params = {
  to: string;
};

export class RenameToolAction extends BaseAction<Params, { t: string }[]> {
  title = 'Rename Tool';
  description = 'Renames a tool and all its references within the graph';
  inputSchema = z.object({
    to: z.string(),
  });
  group = GRAPH_ACTION_GROUP;
  icon = 'pencil';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const toolModel = context.targetedModelOfType(ToolModel);

    if (toolModel === null) {
      return false;
    }

    return toolModel.currentNameInGraph.snapshot() !== null;
  }

  async execute(
    input: z.infer<typeof this.inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const tool = context.targetedModelOfType(ToolModel);

    Assert.assert(tool !== null, 'No targeted tool');
    Assert.assert(graph !== null);

    tool.formState.set({
      ...tool.formState.snapshot(),
      name: input.to,
    });

    const result = tool.compile();

    if (result.isErr()) {
      const error = {
        t: result.error.t,
        message: JSON.stringify(result.error),
      };
      toast.error(error.message);
      return err([error]);
    }

    const stagingResult = await graph.attemptToStageOperations(
      tool.newOperations.snapshot()
    );

    tool.markAsStaged();

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };
      return err([error]);
    }

    return ok({});
  }

  ask(_: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    const targetedTool = context.targetedModelOfType(ToolModel);
    Assert.assert(targetedTool !== null);

    const toolName = targetedTool.currentNameInGraph.snapshot();
    Assert.assert(toolName !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                name={toolName}
                description={this.description}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  name: string;
  description: string;
}> = ({ onRespond, name, description }) => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Rename ${name}`}
      subtitle={description}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <Input
          autoFocus
          placeholder="name"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              await onRespond({ to });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
